import { motion } from 'framer-motion'
import { useContext, useEffect, useRef, useState } from 'react'
import Constants from '../../../../Constants'
import { useUploadMutation } from '../../../../Redux/Api/Api'
import { useImportCardsMutation, useLazyGetOrganisationSettingsQuery } from '../../../../Redux/Api/Organisations';
import { useAppSelector } from '../../../../Redux/store'
import { Hydra } from '../../../../Utils/Hydra'
import BusyArea from '../../../Shared/BusyArea'
import Button from '../../../Shared/Form/Button'
import FilePicker from '../../../Shared/Form/FilePicker'
import Form from '../../../Shared/Form/Form'
import StatusPopups from '../../../Shared/Form/StatusPopups'
import { ModalContext } from '../../../Shared/Modal/Modal'
import CustomSwitch from "../../../Shared/Form/CustomSwitch";
import { useTranslation } from "react-i18next";
import { useChat } from '../../PatrickAssistant/ChatContext';
import { faFileImport } from '@fortawesome/pro-solid-svg-icons';
import PageMaxWidth from '../../../Shared/pageRenderers/PageMaxWidth';
import GenericDatagrid from '../../../Datagrid/GenericDatagrid';
import Title from '../../../Shared/Title';

type FormattedCsvRow = {
    id: number;
    initials: string;
    lastname: string;
    email: string;
    membershipId: string;
    activeFrom: string;
    activeUntil: string;
    type: string;
    dateOfBirth: string;
    location: string;
    phone: string;
    department: string;
    numberOfPeople: string;
    countryCode: string;
    discount: string;
    team: string;
};

type CsvColumn = { field: string; headerName: string; width: number };

const CardsImportPage = () => {
    const modalContext = useContext(ModalContext)
    const { t, i18n } = useTranslation()
    const org_id = useAppSelector((s) => s.organisation.id)!
    const [getOrganisationsSettings, { data: settings }] = useLazyGetOrganisationSettingsQuery()
    const [cardFile, setCardFile] = useState<File | null>(null)
    const [fileIsValid, setFileIsValid] = useState<string>('')
    const [successMessage, setSuccessMessage] = useState<string>('')
    const [csvData, setCsvData] = useState<Array<Array<string>>>()

    const [formattedCsvData, setFormattedCsvData] = useState<FormattedCsvRow[]>([])
    const [csvColumns, setCsvColumns] = useState<CsvColumn[]>([])
    const [busy, setBusy] = useState(false)
    const [isDataOpen, setIsDataOpen] = useState(false)
    const [uploadFile] = useUploadMutation()
    const [importCards] = useImportCardsMutation()

    const { toggleChat, addMessage, isChatOpen } = useChat();

    const [sendMailState, setSendMailState] = useState(false);

    const [patrickAssistantAccess] = useState(() => {
        const savedPatrickAssistantAccess = sessionStorage.getItem('hasPatrickAssistantAccess');
        return savedPatrickAssistantAccess ? savedPatrickAssistantAccess === 'true' : false;
    });


    useEffect(() => {
        if (org_id) getOrganisationsSettings(org_id)
    }, [org_id])

    useEffect(() => {
        if (!cardFile) return

        setFileIsValid('')

            ; (async () => {
                const fileExtension = cardFile.name.split('.').pop()?.toLowerCase();

                if (fileExtension !== 'xlsx' && fileExtension !== 'xls') {
                    return setFileIsValid(t('cards:import:onlyXlsxAllowed') as string);
                }

                // Import XLSXLoader dynamically
                const { XLSXLoader } = await import('../../../../Services/XLSXLoader');
                const loadedData = await XLSXLoader.load(cardFile, t);

                if (loadedData.error) {
                    setFileIsValid(loadedData.error);
                    return;
                }

                if (!loadedData.data || loadedData.data.length === 0) {
                    setFileIsValid('Geen data gevonden in het bestand');
                    return;
                }

                setCsvData(loadedData.data);
                setIsDataOpen(true);

                // Format data for display
                const columnHeaders = [
                    { field: 'id', headerName: t('cards:list:line'), width: 70, getValue: (params: any) => params.row.id + 1, renderCell: (params: any) => params.row.id + 1 },
                    { field: 'initials', headerName: t('cards:list:initials'), width: 100 },
                    { field: 'lastname', headerName: t('cards:list:lastname'), width: 150 },
                    { field: 'email', headerName: t('cards:list:email'), width: 200 },
                    { field: 'membershipId', headerName: t('cards:list:membershipId'), width: 150 },
                    { field: 'activeFrom', headerName: t('cards:list:activeFrom'), width: 150 },
                    { field: 'activeUntil', headerName: t('cards:list:activeUntil'), width: 150 },
                    { field: 'type', headerName: t('cards:list:type'), width: 100 },
                    { field: 'dateOfBirth', headerName: t('cards:list:dateOfBirth'), width: 150 },
                    { field: 'location', headerName: t('cards:list:location'), width: 150 },
                    { field: 'phone', headerName: t('cards:list:phone'), width: 150 },
                    { field: 'department', headerName: t('cards:list:department'), width: 150 },
                    { field: 'numberOfPeople', headerName: t('cards:edit:numberOfPeople'), width: 150, hide: !settings?.familyCards },
                    { field: 'countryCode', headerName: t('cards:list:countryCode'), width: 150 },
                    { field: 'discount', headerName: t('cards:list:discount'), width: 100 },
                    { field: 'team', headerName: t('cards:list:team'), width: 100 }
                ];

                setCsvColumns(columnHeaders);

                const formattedData = loadedData.data.map((row, index) => {
                    // Note: row[0] is the line number added by CSVLoader/XLSXLoader
                    const rowData = {
                        id: index,
                        initials: row[1] || '',
                        lastname: row[2] || '',
                        email: row[3] || '',
                        membershipId: row[4] || '',
                        activeFrom: row[5] || '',
                        activeUntil: row[6] || '',
                        type: row[7] || '',
                        dateOfBirth: row[8] || '',
                        location: row[9] || '',
                        phone: row[10] || '',
                        department: row[11] || '',
                        numberOfPeople: row[12] || '',
                        countryCode: row[13] || '',
                        discount: row[14] || '',
                        team: row[15] || ''
                    };
                    return rowData;
                });

                setFormattedCsvData(formattedData);
            })()

    }, [cardFile, settings, t])

    const trimCSV = (csv: string) => {
        const lines = csv.split('\n').map(line => line.trim());

        // Verwijder lege regels aan het einde
        while (lines[lines.length - 1] === '') {
            lines.pop();
        }

        return lines.join('\n');
    };

    const submit = async ({ cards: file, send }: { cards: File; send: string }) => {
        if (busy) return;
        if (null == file) return setFileIsValid(t("cards:list:addCSV") || " ");
        if (fileIsValid) return setFileIsValid(t("cards:list:problemCSV") || " ");

        if (!(await modalContext.show(t("cards:list:importMessage"), patrickAssistantAccess ? t("cards:list:importMessagePrefixPA") : t("cards:list:importMessagePrefix")))) {
            modalContext.hide();
            return;
        }

        modalContext.hide()

        setBusy(true)
        setSuccessMessage('')
        setFileIsValid('')

        try {
            const file_id = await uploadFile({
                file
            }).unwrap()
            const formatDateString = (dateString: string): string => {
                return dateString.replace(/\s*\(.*?\)\d{2}:\d{2}/, '');
            };
            const selectedDateTime = formatDateString(new Date().toDateString());
            const { successfulRecords, duplicates } = await importCards([org_id, file_id, send == 'true', selectedDateTime]).unwrap();

            if (duplicates.length > 0) {
                setFileIsValid(t('cards:list:duplicatesFound', { duplicates: duplicates.join(', ') }) || " ");
                setBusy(false);
                return;
            }

            if (send === 'true') {
                setSuccessMessage(t('cards:list:importSendsucces', { number: successfulRecords }) || "succes");
            } else {
                setSuccessMessage(t('cards:list:importSuccess', { importedCount: successfulRecords }) ?? "");
            }

            window.scrollTo(0, 0);
        } catch (e) {
            let err = e as Hydra.Error;
            setFileIsValid(err['hydra:description']);
            setBusy(false);
        }
        setTimeout(() => setBusy(false), 200);
        sessionStorage.setItem('cardChanged', 'true');
    }

    const toggleChatVisibility = () => {
        const chatMessagesString = sessionStorage.getItem('chatMessages');
        let chatMessages = [];

        if (chatMessagesString) {
            chatMessages = JSON.parse(chatMessagesString);
        }

        let wasChatOpen = isChatOpen;

        if (!isChatOpen) {
            toggleChat();
        }

        if (chatMessages.length > 0) {
            const lastMessage = chatMessages[chatMessages.length - 1];
            const uploadRegex = /^upload : true \([a-z]{2}\)$/i;

            if (lastMessage.sender === 'assistant' && uploadRegex.test(lastMessage.content)) {
                return;
            }
        }

        const busyPrompt = {
            sender: 'assistant',
            content: 'busy : true (' + i18n.language + ')',
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };
        const downloadPrompt = {
            sender: 'assistant',
            content: 'download : true (' + i18n.language + ')',
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };
        const uploadPrompt = {
            sender: 'assistant',
            content: 'upload : true (' + i18n.language + ')',
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };

        addMessage(busyPrompt);

        setTimeout(() => {
            addMessage(downloadPrompt);
            addMessage(busyPrompt);
        }, wasChatOpen ? 500 : 1000);

        setTimeout(() => {
            addMessage(uploadPrompt);
        }, wasChatOpen ? 1250 : 1750);
    };

    const runMail = () => {
        modalContext.withModal(
            { title: t('cards:list:warningTitle'), body: t('cards:list:activationEmailWarningPrompt') },
            () => setSendMailState(true),
            () => setSendMailState(false)
        )();
    }

    const handleClick = (value: boolean) => {
        if (value) {
            runMail();
        } else {
            setSendMailState(false);
        }
    };

    return (
        <PageMaxWidth pageType='form'>
            <Title text={t('cards:import:importCards')} textNotBold />
            <BusyArea busy={busy}>
                <Form className='mb-2' submit={submit}>
                    <StatusPopups setText={setSuccessMessage} type='success' text={successMessage} />
                    <StatusPopups setText={setFileIsValid} type='error' subtitle={t('cards:import:error') as string} text={fileIsValid} />
                    <div className='mb-2'>
                        <p style={{ whiteSpace: "pre-line" }}>
                            {t('cards:import:description')}
                        </p>
                    </div>
                    <div className='mb-2'>
                        <p style={{ whiteSpace: "pre-line" }}>
                            <b>
                                {t('cards:import:warning')}
                            </b>
                        </p>
                    </div>
                    <div className='mb-4'>
                        <a className='my-2' href={Constants.DOWNLOAD_URL_CARDS_TEMPLATE_XLSX} download>
                            <Button tabIndex={-1} secondary nosubmit title={t('cards:import:downloadTemplate') || 'Download Excel Template'} />
                        </a>
                    </div>
                    {patrickAssistantAccess ? (
                        <div className="relative flex flex-col items-center gap-4 w-full mb-4 p-4 border rounded-lg shadow-sm bg-white">
                            <FilePicker
                                onChange={setCardFile}
                                accept={['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']}
                                label={t('cards:import:cardsFile')}
                                id="cards"
                                className="w-full"
                                noBottomMargin
                            />

                            <p className="text-gray-600 -mt-3">{t('cards:import:or')}</p>

                            <Button
                                nosubmit
                                title={t('mapping:importButton')}
                                action={toggleChatVisibility}
                                className="w-full bg-map-blue text-white rounded-lg hover:bg-map-blue/75 transition-all duration-200"
                            />

                            {cardFile && (
                                <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: 'auto' }}
                                    className="absolute left-6 bottom-0 text-xs hidden sm:flex text-brand opacity-80"
                                >
                                    {t('cards:import:usePatrickSuggestion')}
                                </motion.div>
                            )}
                        </div>

                    ) : (
                        <FilePicker onChange={setCardFile} accept={['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']} label={t('cards:import:cardsFile')} id='cards' />
                    )}

                    {csvData && (
                        <>
                            <Button
                                title={isDataOpen ? t('cards:import:hideExample') : t('cards:import:watchExample')}
                                className='mb-6'
                                nosubmit
                                action={() => setIsDataOpen(s => !s)} />

                            {(isDataOpen && formattedCsvData.length > 0) && (
                                <div className="w-full overflow-x-auto mt-4 mb-8">
                                    <GenericDatagrid
                                        data={formattedCsvData}
                                        columns={csvColumns}
                                        className="w-full"
                                    />
                                </div>
                            )}
                        </>
                    )}

                    <CustomSwitch id='send' label={t('cards:list:sendMail?')} onClick={handleClick} state={sendMailState} />
                    <Button icon={faFileImport} disabled={!cardFile} iconright title={t('cards:list:import')} />
                </Form>
            </BusyArea>
        </PageMaxWidth>
    )
}

export default CardsImportPage
